import { createSelector } from '@reduxjs/toolkit';
import { RootSelector, getStore } from '../../reducers';
import {
  UploaderShape,
  getJobUploadProgress,
  jobIsFailed,
  jobIsSettled,
  jobIsSuccessful,
} from './model';
import { Permission } from '../../../general/model';
import { getCategoriesById, getSelectedId } from '../category/selectors';
import { ALL_CATEGORIES_ID } from '../category/model';
import { getPermissions } from '../app/selectors';
import { getProjectsById, getSelectedIds as getSelectedProjectIds } from '../project/selectors';

export const getUploader: RootSelector<UploaderShape> = createSelector(
  getStore,
  (state) => state.uploader,
);

export const getJobs = createSelector(getUploader, (uploader) => uploader.jobs);

export const getModalOpen = createSelector(getUploader, (uploader) => uploader.modalOpen);

export const getPendingUpload = createSelector(getUploader, (uploader) => uploader.pendingUpload);

export const getCompletedInBackground = createSelector(
  getUploader,
  (uploader) => uploader.completedInBackground,
);

// Aggregate progress/status used by the Upload button and the navigation guard.
export const getUploadStats = createSelector([getJobs], (jobs) => {
  const total = jobs.length;
  const hasJobs = total > 0;
  const allSettled = hasJobs && jobs.every(jobIsSettled);
  const isUploading = hasJobs && !allSettled;
  const totalSize = jobs.reduce((s, j) => s + j.fileSize, 0);
  const uploadedSize = jobs.reduce((s, j) => s + j.fileSize * getJobUploadProgress(j), 0);
  const overallProgress = totalSize > 0 ? uploadedSize / totalSize : 0;
  const failedCount = jobs.filter(jobIsFailed).length;
  const successfulCount = jobs.filter(jobIsSuccessful).length;
  return { hasJobs, allSettled, isUploading, overallProgress, failedCount, successfulCount, total };
});

export const getCategorySelected = createSelector([getSelectedId], (id) => {
  if (id === ALL_CATEGORIES_ID) return false;
  const parsed = Number.parseInt(id, 10);
  return Number.isFinite(parsed) && parsed > 0;
});

export const getSingleProjectSelected = createSelector(
  [getSelectedProjectIds],
  (ids) => ids.length === 1,
);

export const getCanUpload = createSelector([getPermissions], (permissions) =>
  Boolean(permissions[Permission['fs.assetlab_manage_files']]),
);

export const getEnabled = createSelector(
  [getCategorySelected, getSingleProjectSelected, getCanUpload],
  (categorySelected, singleProjectSelected, canUpload) =>
    categorySelected && singleProjectSelected && canUpload,
);

// A single selected project is enough to open the upload: without a category the user
// picks a folder or creates a new one in the upload modal / empty-catalog panel.
// getEnabled (a category is required) still gates drag&drop onto the page.
export const getUploadToProjectEnabled = createSelector(
  [getSingleProjectSelected, getCanUpload],
  (singleProjectSelected, canUpload) => singleProjectSelected && canUpload,
);

export const getUploadDestinationPath: RootSelector<string[]> = createSelector(
  [getSelectedProjectIds, getProjectsById, getSelectedId, getCategoriesById],
  (projectIds, projectsById, categoryId, categoriesById) => {
    if (projectIds.length !== 1) return [];
    const project = projectsById[projectIds[0]];
    if (!project) return [];
    if (categoryId === ALL_CATEGORIES_ID) return [];
    const category = categoriesById[categoryId];
    if (!category) return [];
    const ancestors = category.path
      .map((id) => categoriesById[id]?.name)
      .filter((name): name is string => Boolean(name));
    return [project.name, ...ancestors, category.name];
  },
);
